import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pagesApi, notesApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import ScrollHeading from '@/components/ScrollHeading';
import { ArrowRight, Loader2 } from 'lucide-react';

const Notes = () => {
  const [pageData, setPageData] = useState(null);
  const [notesData, setNotesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField, getContent, isArabic } = useLocalizedContent();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [page, notes] = await Promise.all([
          pagesApi.getPublishedBySlug('notes').catch(() => null),
          notesApi.getPublished()
        ]);
        setPageData(page);
        setNotesData(notes || []);
      } catch (err) {
        console.error('Error fetching notes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sections = pageData?.sections || [];
  const introSection = sections.find(s => s.section_type === 'intro');
  const currentlyReadingSection = sections.find(s => s.section_type === 'currently_reading');
  const introContent = introSection ? getContent(introSection) : null;
  const readingContent = currentlyReadingSection ? getContent(currentlyReadingSection) : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div>
        <ScrollHeading 
          className="heading-display mb-8"
          dataTestId="notes-heading"
        >
          {(pageData && getField(pageData, 'title')) || t('notes.title')}
        </ScrollHeading>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="body-large text-muted-foreground mb-16 max-w-2xl"
        >
          {introContent?.description || t('notes.description')}
        </motion.p>

        {/* Currently Reading Section */}
        {readingContent?.items?.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16 pb-16 border-b border-[#E5E0D6] max-w-3xl"
          >
            <h2 className="caption text-muted-foreground mb-8">
              {readingContent?.label || t('notes.subtitle')}
            </h2>
            <ul className="space-y-4">
              {readingContent.items.map((item, index) => (
                <li key={index} className="body-small text-[#1C1C1C]">
                  {item.author} — {item.url ? (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="italic hover:text-[#8B6F47] transition-colors"
                    >
                      {item.title}
                    </a>
                  ) : (
                    <span className="italic">{item.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </motion.section>
        )}

        {notesData.length === 0 ? (
          <div className="text-center py-16 max-w-3xl">
            <p className="body-small text-muted-foreground">
              {t('notes.noNotes')}
            </p>
          </div>
        ) : (
          <div className="max-w-3xl space-y-12">
            {notesData.map((note, index) => (
              <motion.article
                key={note.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 * index, ease: [0.16, 1, 0.3, 1] }}
                className="border-b border-[#E5E0D6] pb-8"
                data-testid={`note-${note.id}`}
              >
                <Link to={`/notes/${note.slug}`} className="block group">
                  <span className="caption text-[#1C1C1C] block mb-4">
                    {note.date}{note.author && ` · ${note.author}`}
                  </span>
                  
                  <h2 className="heading-medium mb-4 group-hover:text-[#8B6F47] transition-colors" data-testid={`note-title-${note.id}`}>
                    {getField(note, 'title')}
                  </h2>
                  
                  <p className="body-large text-[#1C1C1C] mb-4">
                    {getField(note, 'excerpt')}
                  </p>
                  
                  <span className="caption text-[#1C1C1C] group-hover:text-[#8B6F47] transition-colors inline-flex items-center gap-2">
                    {t('common.readMore')} <ArrowRight size={14} className={isArabic ? 'rotate-180' : ''} />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 pt-8 border-t border-[#E5E0D6]"
        >
          <p className="caption text-muted-foreground">
            {isArabic ? 'الأرشيف قيد التشكيل — المزيد من الملاحظات قريبًا' : 'Archive in formation — More notes forthcoming'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Notes;
